import { InputNumber } from 'antd';
import type { InputNumberProps } from 'antd';
import type { InputNumberRef } from '@rc-component/input-number';
import { forwardRef, useCallback } from 'react';
import type { KeyboardEvent, ClipboardEvent } from 'react';
import {
  formatInputInteger,
  formatInputDecimal,
  parseInputNumber,
} from '@/utils/formatNumber';
import type { InputContext } from './TextInput';
import {
  filterNumberInputKey,
  handleNumberInputPaste,
  setNativeInputValue,
} from './keyFilter';
import { CONTROL_CLASS } from '@/theme/controlSizing';

export type { InputContext };

/**
 * Varian format angka pada {@link NumberInput}.
 *
 * - `integer` → tanpa desimal. Dipakai oleh Quantity & IDR.
 * - `decimal` → 2 desimal. Dipakai oleh MUA & Kurs.
 */
export type NumberFormat = 'integer' | 'decimal';

/**
 * Props {@link NumberInput} — base untuk semua input angka.
 *
 * Tidak meng-expose `formatter`/`parser`/`controls`/`precision` karena nilai
 * tersebut sudah ditentukan oleh {@link NumberFormat} dan derivatif komponen.
 * Properti style/value/onChange/dll tetap diteruskan ke Antd `<InputNumber>`.
 */
export interface NumberInputProps
  extends Omit<
    InputNumberProps<number>,
    'formatter' | 'parser' | 'controls' | 'precision' | 'size' | 'stringMode'
  > {
  /** Konteks pemakaian — menentukan density. Default: `form`. */
  context?: InputContext;
  /** Override paksa size Antd jika perlu. */
  size?: InputNumberProps['size'];
  /** Format angka. Default: `integer`. */
  numberFormat?: NumberFormat;
}

/**
 * BASE: Textbox untuk angka.
 *
 * Sifat universal (tidak boleh dimatikan oleh derivatif):
 * - Rata kanan (via CSS class `booku-number-input`).
 * - Tidak ada simbol mata uang.
 * - Separator standar Indonesia (titik ribuan, koma desimal).
 * - Nilai 0 / null / undefined ditampilkan sebagai string kosong.
 * - Negatif memakai tanda minus (mis. `-1.500`).
 * - **Character filter**: hanya boleh input digit, koma (jika `decimal`), dan
 *   minus (di awal, hanya satu). Titik dicegah — separator ribuan otomatis.
 * - **TANPA stepper** (`controls={false}`, dikunci mati). Kebijakan Booku V2:
 *   tidak ada panah ▲▼ di kolom angka mana pun — lihat textbox-pattern.md
 *   section "KEBIJAKAN: Tanpa Stepper".
 *
 * Sifat varian (diatur via prop):
 * - Integer / 2-desimal via `numberFormat`.
 *
 * Komponen ini biasanya tidak dipakai langsung — gunakan derivatif siap pakai:
 * {@link QuantityInput}, {@link IDRInput}, {@link MUAInput}, {@link KursInput}.
 *
 * Dokumentasi pattern: `.claude/docs/Booku v2/ui-patterns/textbox-pattern.md`
 */
export const NumberInput = forwardRef<InputNumberRef, NumberInputProps>(
  function NumberInput(
    {
      context = 'form',
      size,
      numberFormat = 'integer',
      style,
      className,
      onKeyDown,
      onPaste,
      ...rest
    },
    ref
  ) {
    const resolvedSize = size ?? (context === 'page' ? 'small' : 'middle');
    const formatter =
      numberFormat === 'decimal' ? formatInputDecimal : formatInputInteger;
    const filterOptions = { allowComma: numberFormat === 'decimal' };

    /**
     * Handler keyDown — gabungan beberapa aturan:
     *
     * **Decimal-mode behaviors** (MUA, Kurs):
     * - **Single Comma Swap**: kalau user ketik `,` dan sudah ada `,`, hapus
     *   koma rightmost lalu sisipkan koma di posisi cursor.
     * - **Delete Comma → Drop Decimals**: kalau user hapus `,` (Backspace/
     *   Delete/selection mencakup koma), buang juga semua digit setelah koma.
     *   Saat blur, formatter re-add `,00` otomatis.
     *
     * **Universal character filter** (semua mode):
     * - Hanya izinkan digit, koma (kalau decimal), dan minus (di awal, satu).
     * - Cegah titik (separator ribuan otomatis), huruf, simbol lain.
     * - Modifier combo (Ctrl+A/C/V/X/Z) & control key (Backspace/Tab/Arrow/dll)
     *   tetap dipass-through.
     */
    const handleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLInputElement>) => {
        const input = e.target as HTMLInputElement;
        const value = input.value ?? '';
        const start = input.selectionStart ?? value.length;
        const end = input.selectionEnd ?? value.length;

        // ─── Decimal-mode special behaviors ──────────────────────────────
        if (numberFormat === 'decimal') {
          // Behavior (1): Single Comma Swap.
          if (e.key === ',') {
            const rightmostCommaIdx = value.lastIndexOf(',');
            if (rightmostCommaIdx !== -1) {
              // Selection mencakup koma existing → biarkan default behavior.
              if (end > start && value.substring(start, end).includes(',')) {
                onKeyDown?.(e);
                return;
              }

              e.preventDefault();

              const withoutRightmostComma =
                value.substring(0, rightmostCommaIdx) +
                value.substring(rightmostCommaIdx + 1);

              const cursorAfterRemove =
                start > rightmostCommaIdx ? start - 1 : start;
              const endAfterRemove =
                end > rightmostCommaIdx ? end - 1 : end;

              const newValue =
                withoutRightmostComma.substring(0, cursorAfterRemove) +
                ',' +
                withoutRightmostComma.substring(endAfterRemove);

              setNativeInputValue(input, newValue);

              const newCursorPos = cursorAfterRemove + 1;
              requestAnimationFrame(() => {
                input.setSelectionRange(newCursorPos, newCursorPos);
              });
              return;
            }
            // Belum ada koma → fall through ke filter (akan di-allow karena
            // allowComma=true di decimal mode).
          }

          // Behavior (2): Delete Comma → Drop Decimals.
          if (e.key === 'Backspace' || e.key === 'Delete') {
            const commaIdx = value.indexOf(',');
            if (commaIdx !== -1) {
              let willDeleteComma = false;

              if (start === end) {
                if (e.key === 'Backspace' && start - 1 === commaIdx) {
                  willDeleteComma = true;
                } else if (e.key === 'Delete' && start === commaIdx) {
                  willDeleteComma = true;
                }
              } else {
                if (commaIdx >= start && commaIdx < end) {
                  willDeleteComma = true;
                }
              }

              if (willDeleteComma) {
                e.preventDefault();
                const newValue = value.substring(0, commaIdx);
                setNativeInputValue(input, newValue);
                requestAnimationFrame(() => {
                  input.setSelectionRange(commaIdx, commaIdx);
                });
                return;
              }
            }
            // Bukan delete koma → fall through ke filter (Backspace/Delete
            // multi-char key, akan di-pass-through filter).
          }
        }

        // ─── Universal character filter ──────────────────────────────────
        if (filterNumberInputKey(e, filterOptions)) return; // di-prevent

        onKeyDown?.(e);
      },
      [numberFormat, filterOptions, onKeyDown]
    );

    /**
     * Handler paste — sanitasi clipboard content sebelum disisipkan.
     */
    const handlePaste = useCallback(
      (e: ClipboardEvent<HTMLInputElement>) => {
        handleNumberInputPaste(e, filterOptions);
        onPaste?.(e);
      },
      [filterOptions, onPaste]
    );

    return (
      <InputNumber<number>
        ref={ref}
        size={resolvedSize}
        controls={false}
        precision={numberFormat === 'decimal' ? 2 : 0}
        decimalSeparator=","
        formatter={formatter}
        // Parser Antd diharapkan mengembalikan string (akan dikonversi ke number
        // oleh Antd sesuai tipe generic). String kosong → field kosong (null).
        parser={(v) => parseInputNumber(v) as unknown as number}
        // className `booku-number-input` → CSS global rata kanan inner input
        // (di styles/inputs.css). className `booku-control` → universal control
        // alignment & sizing (di styles/controls.css).
        className={`${CONTROL_CLASS} booku-number-input${className ? ` ${className}` : ''}`}
        // Width default 100% supaya konsisten di Form.Item / Col.
        style={{ width: '100%', ...style }}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        {...rest}
      />
    );
  }
);
